import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate,  } from 'react-router-dom';
import '../../styles/QuestionView.css';
import '../../styles/Stats.css';
import '../../styles/contests/ListeningMaster.css';
import mixpanel from '../../utils/mixpanel'
import { AlertTriangle, Send, X,} from 'lucide-react';
import QuestionLayout, { QUESTION_MODES } from '../../features/questions/Question/QuestionLayout';
import QuestionDisplay, { QUESTION_TYPES } from '../../features/questions/Question/components/QuestionDisplay';
import SessionComplete from '../../features/questions/Question/components/SessionComplete';
// Remove unused import
import HiraganaLoader from '../../components/HiraganaLoader';
import { sendQuestionChat,  } from '../../api/exams';
import { API_URL } from '../../app/constants'
import Mina from '../../assets/images/mina.png';
import { getUserProfile } from '../../api/auth';

// Function to format chat messages with bold text and bullet points
const formatChatMessage = (text) => {
  if (!text) return '';
  
  // First, identify bullet point sequences
  const lines = text.split('\n');
  const processedLines = [];
  let bulletList = null;
  
  lines.forEach((line, index) => {
    // Check if this line is a bullet point (starts with * or - followed by space)
    if (line.trim().match(/^(\*|-)\s+(.+)$/)) {
      // If we don't have an active bullet list, start one
      if (!bulletList) {
        bulletList = [];
      }
      
      // Add this item to the bullet list
      bulletList.push(line.trim().replace(/^(\*|-)\s+/, ''));
    } else {
      // This is not a bullet point
      
      // If we have an active bullet list, add it to the processed lines and reset
      if (bulletList && bulletList.length > 0) {
        processedLines.push({
          type: 'bulletList',
          items: [...bulletList]
        });
        bulletList = null;
      }
      
      // Add the regular line
      processedLines.push({
        type: 'text',
        content: line
      });
    }
  });
  
  // If we have a bullet list at the end, add it
  if (bulletList && bulletList.length > 0) {
    processedLines.push({
      type: 'bulletList',
      items: [...bulletList]
    });
  }
  
  // Now render the processed lines
  return (
    <div>
      {processedLines.map((item, index) => {
        if (item.type === 'bulletList') {
          // Render bullet list
          return (
            <ul key={`list-${index}`} style={{ 
              paddingLeft: '20px',
              margin: '0.5em 0',
              listStyleType: 'disc'
            }}>
              {item.items.map((bulletItem, bulletIndex) => (
                <li key={`bullet-${index}-${bulletIndex}`}>
                  {formatBoldText(bulletItem)}
                </li>
              ))}
            </ul>
          );
        } else {
          // Render regular text
          return (
            <div key={`text-${index}`} style={{ margin: '0.5em 0' }}>
              {formatBoldText(item.content)}
            </div>
          );
        }
      })}
    </div>
  );
};

// Helper function to format bold and italic text
const formatBoldText = (text) => {
  const parts = [];
  let lastIndex = 0;
  
  // Process the text with a combined function to handle both bold and italic formatting
  const processText = (currentText, startIndex) => {
    let currentIndex = startIndex;
    
    // First check for bold (**text**)
    const boldPattern = /\*\*(.*?)\*\*/g;
    boldPattern.lastIndex = currentIndex;
    
    const boldMatch = boldPattern.exec(currentText);
    
    // Then check for italic (*text*) - but ensure it's not part of ** pattern
    const italicPattern = /(?<!\*)\*((?!\*).+?)\*(?!\*)/g;
    italicPattern.lastIndex = currentIndex;
    
    const italicMatch = italicPattern.exec(currentText);
    
    // Determine which pattern matched first (if any)
    if (boldMatch && italicMatch) {
      if (boldMatch.index < italicMatch.index) {
        return processBold(currentText, currentIndex, boldMatch);
      } else {
        return processItalic(currentText, currentIndex, italicMatch);
      }
    } else if (boldMatch) {
      return processBold(currentText, currentIndex, boldMatch);
    } else if (italicMatch) {
      return processItalic(currentText, currentIndex, italicMatch);
    } else {
      // No more formatting found
      if (currentIndex < currentText.length) {
        parts.push(currentText.substring(currentIndex));
      }
      return currentText.length;
    }
  };
  
  // Process bold text
  const processBold = (text, currentIndex, match) => {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }
    
    // Add the bold text
    parts.push(
      <strong key={`bold-${match.index}`}>
        {match[1]}
      </strong>
    );
    
    return match.index + match[0].length;
  };
  
  // Process italic text
  const processItalic = (text, currentIndex, match) => {
    // Add text before the match
    if (match.index > currentIndex) {
      parts.push(text.substring(currentIndex, match.index));
    }
    
    // Add the italic text
    parts.push(
      <em key={`italic-${match.index}`}>
        {match[1]}
      </em>
    );
    
    return match.index + match[0].length;
  };
  
  // Process the text iteratively
  while (lastIndex < text.length) {
    lastIndex = processText(text, lastIndex);
  }
  
  return parts.length > 0 ? parts : text;
};

const MOCK_EXAM_API_URL = `${API_URL}/api/v1/mock-exam`;
const REPORT_ISSUE_API_URL = `${API_URL}/api/v1/panel/report-issue`;

function MockExam() {
  const navigate = useNavigate();
  const { level } = useParams();
  
  // Audio references
  const successSoundRef = useRef(null);
  const wrongSoundRef = useRef(null);
  
  // State management
  const [content, setContent] = useState([]);
  const [currentScriptIndex, setCurrentScriptIndex] = useState(0);
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [revealedSets, setRevealedSets] = useState(new Set());
  const [isAnswerRevealed, setIsAnswerRevealed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timerMinutes, setTimerMinutes] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedIssue, setSelectedIssue] = useState('');
  const [otherIssueText, setOtherIssueText] = useState('');
  const [showExitModal, setShowExitModal] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [viewState, setViewState] = useState('question'); // 'question', 'complete', 'results'
  const [examResults, setExamResults] = useState({
    totalQuestions: 0,
    correctAnswers: 0,
    timeSpent: 0,
    data: null
  });
  const [isTimerPaused, setIsTimerPaused] = useState(false);
  const [showSelectAnswerNotification, setShowSelectAnswerNotification] = useState(false);
  // Additional state for results view
  const [showWrongOnly, setShowWrongOnly] = useState(false);
  const [mockExamId, setMockExamId] = useState(null);
  // const [userProfile, setUserProfile] = useState(null);
  
  // Refs
  const hasFetchedQuestions = useRef(false);
  
  // Structured data for new UI
  const [groupedQuestions, setGroupedQuestions] = useState([]);
  const [currentQuestionSet, setCurrentQuestionSet] = useState([]);

  // Format time for display
  const formatTime = useCallback(() => {
    return `${String(timerMinutes).padStart(2, '0')}:${String(timerSeconds).padStart(2, '0')}`;
  }, [timerMinutes, timerSeconds]);

  // Fetch questions from API
  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();
    
    try {
      const jwt = localStorage.getItem('jwt');
      
      // Get user profile to determine target level if not provided
      let examLevel = level;
      if (!examLevel) {
        const profile = await getUserProfile();
        // setUserProfile(profile);
        examLevel = profile?.target_level || profile?.level || 'N5';
      }
      
      // Fetch mock exam for the level
      const mockExamResponse = await fetch(`${MOCK_EXAM_API_URL}/${examLevel}`, {
        method: "GET",
        headers: {
          'Authorization': `Bearer ${jwt}`
        },
      });
      
      if (!mockExamResponse.ok) {
        if (mockExamResponse.status === 404) {
          throw new Error('No mock exam available for this level');
        }
        throw new Error('Failed to fetch mock exam');
      }
      
      const mockExamData = await mockExamResponse.json();
      console.log("Mock exam data:", mockExamData);
      
      // Get the first mock exam (assuming we want the first available)
      const firstMockExam = mockExamData[0];
      if (!firstMockExam) {
        throw new Error('No mock exam available for this level');
      }
      
      setMockExamId(firstMockExam.exam_id);
      
      // Fetch questions for this mock exam
      const questionsResponse = await fetch(`${MOCK_EXAM_API_URL}/questions/${firstMockExam.exam_id}`, {
        method: "GET",
        headers: {
          'Authorization': `Bearer ${jwt}`
        },
      });
      
      if (!questionsResponse.ok) {
        throw new Error('Failed to fetch mock exam questions');
      }
      
      const questionsData = await questionsResponse.json();
      console.log("Mock exam questions:", questionsData);
      console.log("Sample question structure:", questionsData[0]);

      if (questionsData && Array.isArray(questionsData)) {
        // console.log("API responses with scripts:", questionsData.map(q => ({ id: q.question_id, script: q.script })));
        const formattedResponses = questionsData.map(question => {
          // Convert choices to array format like PersonalizedExam
          let choices = [];
          if (Array.isArray(question.choices)) {
            choices = question.choices;
          } else if (question.choices && question.choices.choices && Array.isArray(question.choices.choices)) {
            choices = question.choices.choices;
          } else if (typeof question.choices === 'object' && question.choices !== null) {
            choices = Object.values(question.choices);
          }
          
          return { 
            ...question, 
            choices: choices, // Normalize to array format
            correct_answer: question.answer || question.correct_answer, // Handle both field names
            script_image: question.script_image, // Keep the script_image data!
            question_urls: question.question_urls, // Keep the question_urls data!
            answer_urls: question.answer_urls, // Keep the answer_urls data!
            selectedAnswer: null, 
            isAnswered: false,
            // Ensure script is properly formatted and a string
            script: question.script ? String(question.script) : "0"
          };
        });
        // console.log("Formatted responses:", formattedResponses);
        setContent(formattedResponses);
        
        // Track exam start
        mixpanel.track('Mock Exam Started', {
          exam_id: firstMockExam.exam_id,
          exam_type: 'mock_exam',
          level: examLevel,
          total_questions: formattedResponses.length,
          start_time: new Date().toISOString(),
          source: 'dashboard'
        });
      } else {
        console.error("Unexpected data structure:", questionsData);
        setError("Unexpected data structure received from the server");
      }
      
      // Ensure minimum loading time of 2 seconds
      const elapsedTime = Date.now() - startTime;
      const minLoadingTime = 2000; // 2 seconds
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
      
    } catch (error) {
      console.error("Error fetching mock exam:", error);
      if (error.message.includes('Failed to fetch')) {
        setError("Unable to connect to the server. Please make sure the backend is running or try again later.");
      } else {
        setError("Error fetching mock exam: " + error.message);
      }
      
      // Ensure minimum loading time even on error
      const elapsedTime = Date.now() - startTime;
      const minLoadingTime = 2000;
      const remainingTime = Math.max(0, minLoadingTime - elapsedTime);
      
      setTimeout(() => {
        setLoading(false);
      }, remainingTime);
    }
  }, [level]);

  // Group questions by script and related ref_ids
  const groupQuestionsByScript = useCallback((questions) => {
    console.log("Grouping questions by script:", questions);
    const groupedQuestions = {};

    // First group by ref_ids (for related questions)
    const relatedGroups = {};

    // Find all questions with related ref_ids and group them
    questions.forEach(question => {
      if (question.ref_id && question.related && Array.isArray(question.related) && question.related.length > 0) {
        // This question is part of a related group
        const groupKey = `related_${question.ref_id}`;

        if (!relatedGroups[groupKey]) {
          // Create a new related group
          relatedGroups[groupKey] = [];
        }

        // Add this question to its related group
        relatedGroups[groupKey].push(question);

        // Also find and add all related questions from the same set
        question.related.forEach(refId => {
          const relatedQuestions = questions.filter(q => q.ref_id === refId);
          relatedQuestions.forEach(relatedQ => {
            // Avoid duplicates
            if (!relatedGroups[groupKey].some(q => q.question_id === relatedQ.question_id)) {
              relatedGroups[groupKey].push(relatedQ);
            }
          });
        });
      }
    });

    console.log("Related question groups:", relatedGroups);

    // Now process all questions, prioritizing related groups
    const processedQuestionIds = new Set();

    // First add all related groups to the result
    Object.values(relatedGroups).forEach(group => {
      if (group.length > 0) {
        const firstQuestion = group[0];
        // Use the script from the first question in the group as the key
        const scriptValue = String(firstQuestion.script || "0");
        const groupKey = `related_script_${scriptValue}`;

        // Add the entire related group, sorted by ref_id (lowest first)
        groupedQuestions[groupKey] = [...group].sort((a, b) => {
          // Sort by ref_id if available
          if (a.ref_id && b.ref_id) {
            return parseInt(a.ref_id) - parseInt(b.ref_id);
          }
          // Fallback to question_id sorting
          return a.question_id - b.question_id;
        });

        // Mark all questions in the group as processed
        group.forEach(q => processedQuestionIds.add(q.question_id));
      }
    });

    // Then process any remaining questions by script
    questions.forEach(question => {
      // Skip questions that were already processed in related groups
      if (processedQuestionIds.has(question.question_id)) {
        return;
      }

      // Convert script to string and check for empty values
      const scriptValue = String(question.script || "0");
      console.log(`Question ${question.question_id} has script: "${scriptValue}"`);

      if (scriptValue === "0" || scriptValue === "" || scriptValue === "null" || scriptValue === "undefined") {
        // For questions without a script or script "0", create a unique key
        groupedQuestions[`single_${question.question_id}`] = [question];
      } else if (!groupedQuestions[scriptValue]) {
        // Create a new group for this script
        groupedQuestions[scriptValue] = [];
        groupedQuestions[scriptValue].push(question);
      } else {
        // Add to existing script group
        groupedQuestions[scriptValue].push(question);
      }

      // Mark as processed
      processedQuestionIds.add(question.question_id);
    });

    const result = Object.values(groupedQuestions);
    console.log("Final grouped questions result:", result);
    return result;
  }, []);

  // Add fullscreen class to body to hide menu toggle
  useEffect(() => {
    // Add fullscreen class to body when component mounts
    document.body.classList.add('review-personalized-fullscreen');
    
    // Clean up function to remove the class when component unmounts
    return () => {
      document.body.classList.remove('review-personalized-fullscreen');
    };
  }, []);

  // Initialize questions
  useEffect(() => {
    if (!hasFetchedQuestions.current) {
      fetchQuestions();
      hasFetchedQuestions.current = true;
    }
  }, [fetchQuestions]);

  // Group questions after fetch
  useEffect(() => {
    if (content.length > 0) {
      const questionSets = groupQuestionsByScript(content);
      setGroupedQuestions(questionSets);
      setCurrentQuestionSet(questionSets[currentScriptIndex] || []);
    }
  }, [content, currentScriptIndex, groupQuestionsByScript]);

  // Timer effect
  useEffect(() => {
    const timerInterval = setInterval(() => {
      // Only update timer if it's not paused
      if (!isTimerPaused) {
        setTimerSeconds(prev => {
          if (prev === 59) {
            setTimerMinutes(min => min + 1);
            return 0;
          }
          return prev + 1;
        });
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isTimerPaused]);  // Add isTimerPaused as dependency
  
  
  // Format text to handle newlines and spacing
  const formatText = useCallback((text) => {
    if (!text || text === '0') return '';

    console.log("Formatting text:", text);
    
    // First, replace {{NEWLINE}} with actual line breaks
    let formattedText = text.replace(/{{NEWLINE}}/g, '\n');
    
    // Ensure Japanese text is displayed properly with proper line breaks
    formattedText = String(formattedText);
    
    // Add special style for Japanese text to ensure it's displayed correctly
    return formattedText;
  }, []);

  // Handle answer selection
  const handleAnswerSelection = (questionId, index) => {
    if (!revealedSets.has(currentScriptIndex)) {
      console.log("Answer selected:", questionId, index);
      setAnsweredQuestions(prev => ({
        ...prev,
        [questionId]: index
      }));
    }
  };

  // Check answers and reveal correct responses
  const handleRevealAnswer = () => {
    const currentQuestions = currentQuestionSet;
    
    // For multiple questions, check if ALL questions are answered
    if (currentQuestions.length > 1) {
      let allAnswered = true;
      
      currentQuestions.forEach(question => {
        if (answeredQuestions[question.question_id] === undefined) {
          allAnswered = false;
        }
      });
      
      // If not all questions are answered, show notification and return early
      if (!allAnswered) {
        setShowSelectAnswerNotification(true);
        // Auto-hide the notification after 3 seconds
        setTimeout(() => {
          setShowSelectAnswerNotification(false);
        }, 3000);
        return;
      }
    } else {
      // For single questions, check if at least one question has been answered
      let hasAnyAnswer = false;
      
      currentQuestions.forEach(question => {
        if (answeredQuestions[question.question_id] !== undefined) {
          hasAnyAnswer = true;
        }
      });
      
      // If no answers are selected, show notification and return early
      if (!hasAnyAnswer) {
        setShowSelectAnswerNotification(true);
        // Auto-hide the notification after 3 seconds
        setTimeout(() => {
          setShowSelectAnswerNotification(false);
        }, 3000);
        return;
      }
    }
    
    setIsAnswerRevealed(true);
    setRevealedSets(prev => new Set(prev).add(currentScriptIndex));
    // Pause the timer when answers are revealed
    setIsTimerPaused(true);
    
    // Play sound based on whether any question in the set was answered correctly
    let anyCorrect = false;
    
    // Check if any question was answered correctly
    currentQuestions.forEach(question => {
      const answerIndex = answeredQuestions[question.question_id];
      if (answerIndex !== undefined) {
        const selectedAnswer = question.choices[answerIndex];
        if (selectedAnswer === question.correct_answer) {
          anyCorrect = true;
        }
      }
    });
    
    // Play appropriate sound
    if (anyCorrect) {
      successSoundRef.current?.play();
    } else {
      wrongSoundRef.current?.play();
    }
  };

  // Navigate to next question set
  const handleNext = () => {
    if (currentScriptIndex < groupedQuestions.length - 1) {
      // Resume the timer when moving to the next question
      setIsTimerPaused(false);
      setCurrentScriptIndex(currentScriptIndex + 1);
      setIsAnswerRevealed(revealedSets.has(currentScriptIndex + 1));
      
      // Reset chat messages when changing questions
      setMessages([]);
      // Also close the chat if it's open
      setShowChat(false);
    } else {
      handleSubmit();
    }
  };

  // Go back to previous question set
  const handlePrevious = () => {
    if (currentScriptIndex > 0) {
      setCurrentScriptIndex(currentScriptIndex - 1);
      setIsAnswerRevealed(revealedSets.has(currentScriptIndex - 1));
      
      // Reset chat messages when changing questions
      setMessages([]);
      // Also close the chat if it's open
      setShowChat(false);
    }
  };

  // Submit exam answers
  const handleSubmit = async () => {
    const allQuestions = content.flat();
    
    // Create the request body with mock_exam_id, responses, and completion_time
    const requestBody = {
      mock_exam_id: mockExamId,
      responses: allQuestions.map(question => {
        const answerIndex = answeredQuestions[question.question_id];
        return {
          question_id: question.question_id,
          selected_answer: answerIndex !== undefined ? question.choices[answerIndex] : null,
          time_taken: Math.round(Math.random() * 30) + 10 // Mock time taken
        };
      }).filter(response => response.selected_answer !== null),
      completion_time: timerMinutes * 60 + timerSeconds // Add total time in seconds
    };

    // Log exact payload for comparison
    console.log("Submitting mock exam data:", requestBody);
    console.log("Submitting mock exam data (stringified):", JSON.stringify(requestBody));
    
    try {
      const jwt = localStorage.getItem('jwt');
      console.log("Using JWT (first 20 chars):", jwt.substring(0, 20) + "...");
      console.log("Submitting to URL:", `${MOCK_EXAM_API_URL}/submit`);
      
      // Log the actual structure of a sample question
      if (allQuestions.length > 0) {
        console.log("Sample question structure:", {
          original: allQuestions[0],
          choices_type: typeof allQuestions[0].choices,
          is_array: Array.isArray(allQuestions[0].choices),
          sample_choice: allQuestions[0].choices[0],
          sample_choice_type: typeof allQuestions[0].choices[0]
        });
      }
      
      const response = await fetch(`${MOCK_EXAM_API_URL}/submit`, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${jwt}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify(requestBody)
      });
      
      // Log full response for debugging
      console.log("Response status:", response.status);
      console.log("Response headers:", Object.fromEntries([...response.headers]));
      
      if (!response.ok) {
        let errorText = "";
        try {
          const errorData = await response.json();
          errorText = JSON.stringify(errorData);
          console.log("Submit error (JSON):", errorData);
        } catch (e) {
          errorText = await response.text();
          console.log("Submit error (text):", errorText);
        }
        throw new Error(`Failed to submit exam: ${response.status} - ${errorText}`);
      }

      // Calculate completion metrics
      const flatContent = content.flat();
      const correctAnswers = Object.keys(answeredQuestions).reduce((count, questionId) => {
        const question = flatContent.find(q => q.question_id.toString() === questionId);
        if (!question) return count;
        
        const answerIndex = answeredQuestions[questionId];
        const selectedAnswer = question.choices[answerIndex];
        const correctAnswer = question.correct_answer;
        
        return (selectedAnswer === correctAnswer) ? count + 1 : count;
      }, 0);
      
      const totalQuestions = Object.keys(answeredQuestions).length;
      const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
      const completionTime = timerMinutes * 60 + timerSeconds;
      
      mixpanel.track('Mock Exam Completed', {
        exam_id: mockExamId,
        exam_type: "mock_exam",
        level: level,
        score: score,
        correct_answers: correctAnswers,
        total_questions: totalQuestions,
        completion_time_seconds: completionTime,
        completion_time_minutes: Math.round(completionTime / 60 * 10) / 10,
        completion_date: new Date().toISOString(),
        source: 'dashboard'
      });
      
      // Calculate correct answers for the score
      try {
        const flatContent = content.flat(); // Flatten if grouped
        const correctAnswers = Object.keys(answeredQuestions).reduce((count, questionId) => {
          const question = flatContent.find(q => q.question_id.toString() === questionId);
          if (!question) return count;
          
          const answerIndex = answeredQuestions[questionId];
          const selectedAnswer = question.choices[answerIndex];
          const correctAnswer = question.correct_answer;
          
          return (selectedAnswer === correctAnswer) ? count + 1 : count;
        }, 0);
        
        const totalQuestions = Object.keys(answeredQuestions).length;
        const score = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;
        
        console.log("Score calculation:", { correctAnswers, totalQuestions, score });
        
        // Prepare the exam results data with calculated score
        setExamResults({
          totalQuestions: totalQuestions,
          correctAnswers: correctAnswers,
          score: score,
          timeSpent: timerMinutes * 60 + timerSeconds,
          data: response
        });
      } catch (error) {
        console.error("Error calculating score:", error);
        // Fallback to default values if calculation fails
        setExamResults({
          totalQuestions: groupedQuestions.length,
          correctAnswers: 0,
          score: 0,
          timeSpent: timerMinutes * 60 + timerSeconds,
          data: response
        });
      }
      
      console.log("Mock exam submitted successfully");
      console.log("Setting view state to complete, confetti should display");
      
      // Change view state to show the session complete screen
      setViewState('complete');
    } catch (error) {
      console.error("Failed to submit mock exam:", error.message);
      alert(`Failed to submit exam: ${error.message}`);
    }
  };

  // Handle exit button click
  const handleExit = (e) => {
    e?.preventDefault();
    setShowExitModal(true);
  };

  // Handle Ask Mina button click
  const handleAskMina = () => {
    setShowChat(true);
    // If first time opening chat, add welcome message
    if (messages.length === 0) {
      setMessages([
        { id: 1, sender: 'bot', text: 'How can I help you with this question?' }
      ]);
    }
  };

  // Handle closing the chat
  const handleCloseChat = () => {
    setShowChat(false);
  };

  // Handle sending message to chat
  const handleSendMessage = async (e) => {
    e?.preventDefault();
    if (!inputValue.trim() || isTyping) return;
    
    // Add user message
    const userInput = inputValue;
    const userMessage = { id: Date.now(), sender: 'user', text: userInput };
    setMessages(prevMessages => [...prevMessages, userMessage]);
    setInputValue('');
    setIsTyping(true);
    
    try {
      // Get current question
      const currentQuestion = currentQuestionSet[0];
      
      // Get the user's selected answer index
      const answerIndex = answeredQuestions[currentQuestion?.question_id];
      
      // Get the user's selected answer text
      const userAnswer = answerIndex !== undefined 
        ? currentQuestion?.choices[answerIndex] 
        : null;
      
      console.log('User selected answer:', userAnswer);
      
      // Format the chat history for the API (include ALL messages including current one)
      const conversationHistory = [...messages, userMessage]
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'Mina',
          content: msg.text
        }));
      
      // Prepare question context for the API - include all related questions if available
      const allQuestionsInSet = currentQuestionSet && currentQuestionSet.length > 1 ? currentQuestionSet : [currentQuestion];
      
      const questionContext = {
        question_id: currentQuestion?.question_id,
        question_text: currentQuestion?.question,
        options: currentQuestion?.choices,
        correct_answer: currentQuestion?.correct_answer,
        explanation: currentQuestion?.explanation,
        user_answer: userAnswer,
        // Include all related questions for better AI context
        all_questions: allQuestionsInSet.map(q => {
          const qAnswerIndex = answeredQuestions[q.question_id];
          const qUserAnswer = qAnswerIndex !== undefined ? q.choices[qAnswerIndex] : null;
          return {
            question_id: q.question_id,
            question_text: q.question,
            options: q.choices,
            correct_answer: q.correct_answer,
            explanation: q.explanation,
            user_answer: qUserAnswer,
            script: q.script
          };
        }),
        script: currentQuestion?.script,
        image_path: imageUrl
      };
      
      console.log('Question context:', questionContext);
      
      let responseData;
      
      try {
        // Try to use our updated sendQuestionChat API with the right parameters
        responseData = await sendQuestionChat(questionContext, userInput, conversationHistory);
        console.log('API response:', responseData);
      } catch (apiError) {
        console.error('Error calling API, falling back to mock response:', apiError);
        
        // If the API call fails, provide a meaningful fallback response
        let mockContent = `I'll help explain this question. The question is asking about "${questionContext.question_text}".`;
        
        if (questionContext.options) {
          mockContent += `\n\nThe correct answer is "${questionContext.correct_answer}".`;
          
          if (questionContext.user_answer) {
            mockContent += `\n\nYou selected "${questionContext.user_answer}", which is ${
              questionContext.user_answer === questionContext.correct_answer ? 'correct!' : 'incorrect.'
            }`;
          }
        }
        
        if (questionContext.explanation) {
          mockContent += `\n\n${questionContext.explanation}`;
        } else {
          mockContent += `\n\nThere is no additional explanation provided for this question. Would you like me to analyze it further?`;
        }
        
        responseData = {
          choices: [{
            message: {
              content: mockContent
            }
          }]
        };
      }
      
      // Extract the bot's response text - handle different response formats
      let botResponseText;
      
      // Check if the response contains a JSON part
      if (typeof responseData === 'string') {
        // If the response is a string, try to split out any JSON part
        const jsonStartIndex = responseData.indexOf('{');
        if (jsonStartIndex > 0) {
          // Extract the text part before the JSON
          botResponseText = responseData.substring(0, jsonStartIndex).trim();
        } else {
          botResponseText = responseData;
        }
      } else if (responseData.choices && responseData.choices[0] && responseData.choices[0].message) {
        // OpenAI-like response format
        botResponseText = responseData.choices[0].message.content;
      } else if (responseData.response) {
        // Our custom API response format
        botResponseText = responseData.response;
      } else if (responseData.text) {
        // Alternative response format
        botResponseText = responseData.text;
      } else if (responseData.history) {
        // If we have a history array from the backend
        const systemMessages = responseData.history.filter(msg => msg.role === 'system');
        // Get the last system message which should be the AI response
        if (systemMessages.length > 0) {
          botResponseText = systemMessages[systemMessages.length - 1].content;
        } else {
          botResponseText = "I'm sorry, I couldn't process your question properly.";
        }
      } else {
        // Fallback if the response format is unexpected
        botResponseText = "I'm sorry, but I couldn't process your question properly. Please try asking something else.";
      }
      
      // Add bot message with the AI response
      const botMessage = { 
        id: Date.now(), 
        sender: 'bot', 
        text: botResponseText
      };
      setMessages(prevMessages => [...prevMessages, botMessage]);
    } catch (error) {
      console.error('Error processing chat message:', error);
      // Fallback response in case of error
      const errorMessage = { 
        id: Date.now(), 
        sender: 'bot', 
        text: 'I apologize, but I encountered an error while processing your question. Please try again later.' 
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  // Open report issue modal
  const handleReportIssue = () => {
    setShowReportModal(true);
  };

  // Submit issue report
  const handleSubmitIssue = async () => {
    try {
      const jwt = localStorage.getItem('jwt');
      const issueMessage = selectedIssue === "Other" ? otherIssueText : selectedIssue;
      
      // Get current question ID
      const currentQuestion = currentQuestionSet[0];
      
      const response = await fetch(REPORT_ISSUE_API_URL, {
        method: "POST",
        headers: {
          'Authorization': `Bearer ${jwt}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          question_id: currentQuestion?.question_id,
          issue_message: issueMessage,
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to submit issue: ${response.status} ${errorText}`);
      }

      alert("Issue reported successfully!");
      setShowReportModal(false);
      setSelectedIssue('');
      setOtherIssueText('');
    } catch (error) {
      console.error("Failed to submit issue:", error.message);
    }
  };

  // Loading state
  if (loading) {
    return <HiraganaLoader />;
  }

  // Error state
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  // No questions state
  if (!currentQuestionSet || currentQuestionSet.length === 0) {
    return <div className="error-message">No questions available.</div>;
  }

  // Get current question
  const currentQuestion = currentQuestionSet[0];
  const hasScript = currentQuestion?.script && currentQuestion.script !== "0" && currentQuestion.script !== 0;
  // Only use image URL if provided by API
  let imageUrl = null;
  if (currentQuestion?.question_urls?.urls && Array.isArray(currentQuestion.question_urls.urls) && currentQuestion.question_urls.urls.length > 0) {
    imageUrl = currentQuestion.question_urls.urls[0];
  } else if (currentQuestion?.script_image?.urls?.[0]) {
    imageUrl = currentQuestion.script_image.urls[0];
  } else if (currentQuestion?.script_image_url) {
    imageUrl = currentQuestion.script_image_url;
  }
  // Remove automatic URL building - only show images when API provides URL
  
  console.log("QUESTION INFO:", {
    id: currentQuestion?.question_id,
    hasScript: hasScript,
    ref_id: currentQuestion?.ref_id,
    question_urls: currentQuestion?.question_urls,
    script_image: currentQuestion?.script_image,
    script_image_url: currentQuestion?.script_image_url,
    imageUrl: imageUrl
  });
  // const remainingQuestions = groupedQuestions.length - currentScriptIndex;
  
  // Prepare questions for the QuestionDisplay component
  let options = [];
  let questions = [];

  // First, check if this is a related question group or a script-based group
  const isRelatedGroup = currentQuestionSet.some(q =>
    q.ref_id && q.related && Array.isArray(q.related) && q.related.length > 0
  );

  // For related questions or multiple questions with same script
  if ((isRelatedGroup || hasScript) && currentQuestionSet.length > 1) {
    // Multiple questions with a reading passage or related questions
    questions = currentQuestionSet.map((q, idx) => ({
      id: q.question_id,
      text: q.question,
      ref_id: q.ref_id,
      options: [
        ...q.choices.map((choice, index) => ({ id: index, text: choice }))
      ],
      correctAnswer: q.correct_answer,
      explanation: q.explanation
    }));

    // console.log("Displaying multiple questions:", questions);
  } else {
    // Single question
    options = [
      ...currentQuestion.choices.map((choice, index) => {
        let answerImageUrl = null;
        if (currentQuestion?.answer_urls?.urls && Array.isArray(currentQuestion.answer_urls.urls) && currentQuestion.answer_urls.urls[index]) {
          answerImageUrl = currentQuestion.answer_urls.urls[index];
        }
        return { 
          id: index, 
          text: choice,
          imageUrl: answerImageUrl
        };
      })
    ];

    // console.log("Displaying single question with options:", options);
  }

  // Handle viewing results after completion screen
  const handleViewResults = () => {
    // Navigate to mock exam results page
    navigate('/mock-exam-results');
  };

  // Handle back to dashboard
  const handleBackToDashboard = () => {
    // Navigate back to dashboard
    navigate('/');
  };

  // Render based on view state
  if (viewState === 'complete') {
    return (
      <SessionComplete 
        score={Math.round((examResults.correctAnswers / examResults.totalQuestions) * 100) || 0}
        timeInSeconds={examResults.timeSpent}
        onViewResults={handleViewResults}
        buttonText="View results"
      />
    );
  }
  
  if (viewState === 'results') {
    // Format questions for ResultsReview component
    const flatContent = content.flat(); // Ensure content is flattened
    console.log("Preparing results with answeredQuestions:", answeredQuestions);
    
    const reviewQuestions = flatContent.map(question => {
      const answerIndex = answeredQuestions[question.question_id];
      console.log(`Question ${question.question_id} answer index:`, answerIndex);
      
      // If no answer was selected
      if (answerIndex === undefined) {
        return {
          id: question.question_id,
          text: question.question,
          isCorrect: false,
          isBookmarked: false
        };
      }
      
      // Get the selected answer from the choices array using the index
      const selectedAnswer = question.choices[answerIndex];
      console.log(`Question ${question.question_id} selected:`, selectedAnswer, "correct:", question.correct_answer);
      
      // Compare with correct answer
      const isCorrect = selectedAnswer === question.correct_answer;
      
      return {
        id: question.question_id,
        text: question.question,
        isCorrect: isCorrect,
        isBookmarked: false
      };
    });
    
    // Format time from seconds to mm:ss
    const formatTime = (seconds) => {
      const mins = Math.floor(seconds / 60);
      const secs = seconds % 60;
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    // Calculate correct answers percentage
    const correctAnswers = reviewQuestions.filter(q => q.isCorrect).length;
    const totalQuestions = reviewQuestions.length;
    const scorePercentage = totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0;

    // New study results page design based on study_result.png
    return (
      <div className="analytics review-personalized-fullscreen" style={{
        padding: '20px 16px',
        fontFamily: "'Manrope', sans-serif",
        background: 'white'
      }}>
        {/* Header with title and buttons */}
        <div className="analytics-header" style={{
          maxWidth: '1000px',
          margin: '0 auto 24px auto'
        }}>
          <h1>Mock Exam Results</h1>

          <div style={{
            display: 'flex',
            gap: '8px'
          }}>
            <button
              onClick={() => window.open('https://www.instagram.com/getmina.co/', '_blank')}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #9333EA',
                background: '#FFFFFF',
                color: '#9333EA',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              Send Feedback
            </button>

            <button
              onClick={handleBackToDashboard}
              style={{
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                background: '#9333EA',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer',
                whiteSpace: 'nowrap'
              }}
            >
              <span className="desktop-only" style={{ display: 'none' }}>Return to Dashboard</span>
              <span className="mobile-only">Dashboard</span>
            </button>
          </div>
        </div>

        {/* Performance Summary Card */}
        <div className="analytics-section" style={{
          maxWidth: '1000px',
          margin: '0 auto 24px auto'
        }}>
          <div className="section-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="section-icon">
              <path d="M12 20V10"/>
              <path d="M18 20V4"/>
              <path d="M6 20v-4"/>
            </svg>
            <h2>Performance Summary</h2>
          </div>

          <div className="summary-grid">
            {/* Score Card */}
            <div className="summary-stat" style={{textAlign: 'center', padding: '20px'}}>
              <div className="stat-header" style={{textAlign: 'center'}}>Score</div>

              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '8px'}}>
                <div className="stat-value" style={{fontSize: '32px', marginBottom: '8px'}}>{scorePercentage}%</div>
                <div style={{color: '#6B7280', fontSize: '16px'}}>{correctAnswers}/{totalQuestions}</div>
              </div>
            </div>

            {/* Time Card */}
            <div className="summary-stat" style={{textAlign: 'center', padding: '20px'}}>
              <div className="stat-header" style={{textAlign: 'center'}}>Time Spent</div>

              <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '8px'}}>
                <div className="stat-value" style={{fontSize: '32px', marginBottom: '8px'}}>{formatTime(examResults.timeSpent)}</div>
              </div>

              <div style={{color: '#6B7280', fontSize: '14px', textAlign: 'center'}}>
                Recommended time: 60:00
              </div>
            </div>
          </div>
        </div>

        {/* Questions Results Section */}
        <div className="analytics-section" style={{
          maxWidth: '1000px',
          margin: '0 auto'
        }}>
          <div className="section-title">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="section-icon">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="m9 12 2 2 4-4"></path>
            </svg>
            <h2>Answered Questions Results</h2>
          </div>

          {/* Filter checkbox */}
          <div style={{
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#F5F3FF',
            padding: '10px 12px',
            borderRadius: '8px'
          }}>
            <input
              type="checkbox"
              id="wrongOnly"
              style={{
                marginRight: '8px',
                width: '16px',
                height: '16px',
                accentColor: '#9333EA'
              }}
              checked={showWrongOnly}
              onChange={(e) => setShowWrongOnly(e.target.checked)}
            />
            <label htmlFor="wrongOnly" style={{ fontSize: '14px', color: '#4B5563', fontWeight: '500' }}>
              Show wrong answers only
            </label>
          </div>

          {/* Questions Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '16px'
          }}>
            {reviewQuestions
              .filter(question => !showWrongOnly || !question.isCorrect)
              .map((question, index) => (
              <div
                key={question.id}
                style={{
                  border: '1px solid #E5E7EB',
                  borderRadius: '8px',
                  padding: '14px',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  position: 'relative',
                  height: '170px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={() => {
                  // Navigate to review page
                  navigate(`/mock-exam-review`);
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                  e.currentTarget.style.borderColor = '#9333EA';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                  e.currentTarget.style.borderColor = '#E5E7EB';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: '8px'
                }}>
                  <div style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                    Question {index + 1}
                  </div>

                  <div>
                    {question.isCorrect ? (
                      <div style={{ color: '#10B981', fontSize: '24px' }}>✓</div>
                    ) : (
                      <div style={{ color: '#EF4444', fontSize: '24px' }}>✕</div>
                    )}
                  </div>
                </div>

                <div style={{
                  fontSize: '14px',
                  color: '#111827',
                  marginBottom: '16px',
                  flexGrow: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {question.text}
                </div>

                {!question.isCorrect && (
                  <div style={{
                    fontSize: '14px',
                    color: '#10B981',
                    display: 'flex',
                    marginTop: 'auto'
                  }}>
                    <span style={{ fontWeight: '600', marginRight: '4px' }}>Correct:</span>
                    {/* Get correct answer from the original content array */}
                    <span>{flatContent.find(q => q.question_id === question.id)?.correct_answer || 'N/A'}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Question solving view
  return (
    <>
      {/* Audio elements */}
      <audio ref={successSoundRef} src="/sounds/success.mp3" preload="auto"></audio>
      <audio ref={wrongSoundRef} src="/sounds/wrong.mp3" preload="auto"></audio>
      <QuestionLayout
        timer={formatTime()}
        totalQuestions={groupedQuestions.length}
        currentQuestion={currentScriptIndex + 1}
        onExit={handleExit}
        isAnswered={isAnswerRevealed}
        mode={QUESTION_MODES.SOLVING}
        footer={
          <>
            <button 
              className="report-btn" 
              onClick={handleReportIssue}
            >
              <AlertTriangle size={20} />
              <span className="report-text">Report Issue</span>
            </button>
            
            <button 
              className="ask-mina-btn" 
              onClick={handleAskMina}
              style={{ display: isAnswerRevealed ? 'flex' : 'none' }}
            >
              Ask Mina
            </button>
            
            <div className="navigation-buttons">
              <button 
                className="nav-btn previous" 
                disabled={currentScriptIndex === 0}
                onClick={handlePrevious}
              >
                Previous
              </button>
              
              <button 
                className="nav-btn next" 
                onClick={isAnswerRevealed ? handleNext : handleRevealAnswer}
              >
                {isAnswerRevealed ? 
                  (currentScriptIndex === groupedQuestions.length - 1 ? 'Finish' : 'Next') : 
                  'Check Answer'}
              </button>
            </div>
          </>
        }
      >
        <QuestionDisplay
          type={(hasScript || isRelatedGroup) && currentQuestionSet.length > 1 ? QUESTION_TYPES.MULTIPLE : QUESTION_TYPES.SINGLE}
          title={`Question ${currentScriptIndex + 1}`}
          instruction={currentQuestion.instruction}
          script={hasScript ? currentQuestion.script : null} /* Pass the script directly */
          questionText={currentQuestion.question}
          readingText={hasScript ? currentQuestion.script : null} /* Only pass script if it exists */
          questions={questions}
          options={options}
          selectedAnswer={answeredQuestions[currentQuestion.question_id]}
          selectedAnswers={answeredQuestions}
          isAnswered={isAnswerRevealed}
          correctAnswer={currentQuestion.correct_answer}
          onAnswerSelect={(isRelatedGroup || hasScript) && currentQuestionSet.length > 1
            ? (questionIndex, answerId) => {
                console.log("Multiple question answer:", questionIndex, answerId);
                if (questions[questionIndex]) {
                  const questionId = questions[questionIndex].id;
                  console.log("Found question ID:", questionId);
                  handleAnswerSelection(questionId, answerId);
                } else {
                  console.error("No question found at index:", questionIndex);
                }
              }
            : (answerId) => handleAnswerSelection(currentQuestion.question_id, answerId)
          }
          explanation={isAnswerRevealed ? currentQuestion.explanation : null}
          formatText={formatText}
          imageUrl={imageUrl}
        />
      </QuestionLayout>

      {/* Exit Modal */}
      {showExitModal && (
        <div className="listening-master-modal-overlay">
          <div className="listening-master-modal-content">
            <h3>Exit</h3>
            <p>Are you sure you want to exit?<br/>Your progress will be lost.</p>
            <div className="listening-master-modal-buttons">
              <button 
                className="listening-master-modal-cancel"
                onClick={() => setShowExitModal(false)}
              >
                Cancel
              </button>
              <button 
                className="listening-master-modal-confirm"
                onClick={() => {
                  mixpanel.track('Cancel Mock Exam', {
                    exam_id: mockExamId,
                    question_id: currentQuestion?.question_id,
                    question_number: currentScriptIndex + 1,
                  });
                  navigate('/');
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Report Issue Modal */}
      {showReportModal && (
        <div className="modal-overlay">
          <div className="report-modal">
            <h3>
              Report Issue
              <button onClick={() => setShowReportModal(false)}>×</button>
            </h3>
            <div className="report-options">
              {[
                { value: 'None of the options is correct', label: 'None of the options is correct' },
                { value: 'Question has incorrect or missing data', label: 'Question has incorrect or missing data' },
                { value: 'Questions/Options/Infographics are not visible properly or missing', label: 'Questions/Options/Infographics are not visible properly or missing' },
                { value: 'Question or option has a typo', label: 'Question or option has a typo' },
                { value: 'The question is confusing or unclear', label: 'The question is confusing or unclear' },
                { value: 'Other', label: 'Other' }
              ].map(option => (
                <label key={option.value} className="report-option">
                  <input 
                    type="radio" 
                    name="issue" 
                    value={option.value}
                    checked={selectedIssue === option.value}
                    onChange={(e) => setSelectedIssue(e.target.value)}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            
            {selectedIssue === 'Other' && (
              <div className="other-description">
                <label>Description of issue:</label>
                <textarea
                  value={otherIssueText}
                  onChange={(e) => setOtherIssueText(e.target.value)}
                  placeholder="Please describe the issue..."
                  rows={4}
                />
              </div>
            )}
            
            <button 
              className="submit-issue" 
              onClick={handleSubmitIssue}
              disabled={selectedIssue === 'Other' && !otherIssueText.trim()}
            >
              Submit Issue
            </button>
          </div>
        </div>
      )}

      {/* Context Chat Component */}
      <div className={`context-chat ${showChat ? 'visible' : ''}`}>
        <div className="context-chat-header">
          <h3>Chat with Mina</h3>
          <button 
            className="context-chat-close" 
            onClick={handleCloseChat}
            aria-label="Close chat"
          >
            <X size={20} />
          </button>
        </div>

        <div className="context-chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`context-chat-message ${message.sender}`}>
              {message.sender === 'bot' && (
                <div className="bot-avatar">
                  <img src={Mina} alt="Mina" className="avatar-logo" style={{ width: '24px', height: '24px' }} />
                </div>
              )}
              <div className="message-content">
                {formatChatMessage(message.text)}
              </div>
            </div>
          ))}
          
          {isTyping && (
            <div className="context-chat-message bot">
              <div className="bot-avatar">
                <img src={Mina} alt="Mina" className="avatar-logo" style={{ width: '24px', height: '24px' }} />
              </div>
              <div className="message-content">
                <span className="typing-indicator">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="context-chat-input-container">
          <form onSubmit={handleSendMessage}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              disabled={isTyping}
            />
            <button 
              type="submit" 
              className="send-message" 
              disabled={!inputValue.trim() || isTyping}
              aria-label="Send message"
            >
              <Send size={20} />
            </button>
          </form>
        </div>
      </div>
      
      {/* Select Answer Notification */}
      {showSelectAnswerNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#9333EA',
          color: 'white',
          padding: '12px 24px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(147, 51, 234, 0.3)',
          zIndex: 1200,
          fontSize: '14px',
          fontWeight: '500',
          animation: 'slideInDown 0.3s ease-out',
          backdropFilter: 'blur(4px)'
        }}>
          {currentQuestionSet.length > 1 ? 'Please answer all questions' : 'Please select an answer'}
        </div>
      )}

      {/* Chat Overlay */}
      <div 
        className={`context-chat-overlay ${showChat ? 'visible' : ''}`}
        onClick={handleCloseChat}
      />
    </>
  );
}

export default MockExam;